import React from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Chip,
  Paper,
  Tooltip,
  Radio,
  RadioGroup,
  FormControl,
  Checkbox,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material'
import Alert from '@mui/material/Alert'
import { PlayArrow as TestIcon, Code as CodeIcon } from '@mui/icons-material'
import { AutoCompleteInput } from '@components/AutoCompleteInput'

const TEMPLATE_VARIABLES = [
  { key: '{{transcript}}', description: 'Full call transcript' },
  { key: '{{callNotes}}', description: "Agent's call notes" },
  { key: '{{campaignName}}', description: 'Campaign name' },
  { key: '{{agentName}}', description: 'Agent who handled the call' },
  { key: '{{callerName}}', description: "Caller's name" },
  { key: '{{disposition}}', description: 'Call disposition' },
  { key: '{{handleTime}}', description: 'Call duration in seconds' },
]

function QACriteriaDialog({
  open,
  onClose,
  editingCriterion = null,
  classes,
  formData,
  setFormData,
  generatedPrompt,
  promptLoading,
  evaluationTemplates,
  allCampaigns,
  selectedCampaign = null,
  selectedCampaignsForCreate,
  setSelectedCampaignsForCreate,
  getCriteriaCount,
  toggleCheckItem,
  handleTypeChange,
  getCurrentTemplate,
  insertVariable,
  insertSamplePrompt,
  testCallId,
  setTestCallId,
  testResult = null,
  testLoading,
  handleTestPrompt,
  campaignCalls,
  callsLoading,
  callInputMode,
  setCallInputMode,
  manualCallId,
  setManualCallId,
  callValidation = null,
  setCallValidation,
  validating,
  verifyCallId,
  fetchCampaignCalls,
  saving,
  onSave,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: 16,
          backgroundColor: 'var(--color-card-bg)',
          color: 'var(--color-text-primary)',
        },
      }}
    >
      <DialogTitle style={{ borderBottom: '1px solid var(--color-border-light)' }}>
        <Typography variant="h6" style={{ fontWeight: 600, color: 'var(--color-deep-blue)' }}>
          {editingCriterion ? 'Edit Custom Criterion' : 'Add Custom Criterion'}
        </Typography>
      </DialogTitle>
      <DialogContent className={classes.dialogContent} style={{ padding: 24 }}>
        {/* Campaign Selection - only for new criteria */}
        {!editingCriterion && (
          <Box marginBottom={3}>
            <Typography variant="subtitle2" className={classes.sectionTitle}>
              Apply to Campaigns
            </Typography>
            <Typography variant="body2" className={classes.sectionSubtitle}>
              Select one or more campaigns for this criterion
            </Typography>
            <AutoCompleteInput
              multiple
              options={allCampaigns}
              value={selectedCampaignsForCreate}
              onChange={(_, newValue) => setSelectedCampaignsForCreate(newValue)}
              badges={[
                {
                  getCount: (option) => getCriteriaCount(option.name),
                  label: 'criteria',
                  color: 'neutral',
                },
              ]}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={option.name}
                    label={option.displayName}
                    {...getTagProps({ index })}
                    style={{
                      backgroundColor: 'rgba(59, 130, 246, 0.1)',
                      color: 'var(--color-deep-blue)',
                    }}
                  />
                ))
              }
              showSearchIcon={false}
              textFieldProps={{ InputProps: { style: { borderRadius: 12 } } }}
              placeholder="Select campaigns..."
              noOptionsText="No campaigns found"
            />
            {selectedCampaignsForCreate.length > 1 && (
              <Alert severity="info" style={{ marginTop: 12, borderRadius: 8 }}>
                This criterion will be created for {selectedCampaignsForCreate.length} campaigns
              </Alert>
            )}
          </Box>
        )}

        {/* Criterion Name */}
        <TextField
          label="Criterion Name"
          fullWidth
          margin="normal"
          variant="outlined"
          value={formData.criterionName}
          onChange={(e) => setFormData({ ...formData, criterionName: e.target.value })}
          placeholder="e.g., Identity Verification, HIPAA Compliance"
          InputProps={{ style: { borderRadius: 12 } }}
        />

        {/* Active Toggle - shown when editing */}
        {editingCriterion && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            style={{
              backgroundColor: formData.isActive ? '#F0FDF4' : '#FEF2F2',
              padding: '12px 16px',
              borderRadius: 12,
              marginTop: 16,
              marginBottom: 8,
              border: formData.isActive ? '1px solid #BBF7D0' : '1px solid #FECACA',
            }}
          >
            <Box>
              <Typography
                variant="subtitle2"
                style={{ fontWeight: 600, color: formData.isActive ? '#166534' : '#991B1B' }}
              >
                {formData.isActive ? 'Criterion is Active' : 'Criterion is Inactive'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {formData.isActive
                  ? 'This criterion will be evaluated during QA'
                  : 'This criterion will NOT be evaluated during QA'}
              </Typography>
            </Box>
            <Switch
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              color="primary"
            />
          </Box>
        )}

        {/* Evaluation Type Selection */}
        <Box className={classes.typeSelector}>
          <Typography variant="subtitle1" className={classes.sectionTitle}>
            Evaluation Type
          </Typography>
          <Typography variant="body2" className={classes.sectionSubtitle}>
            Choose a predefined evaluation template or create a custom prompt
          </Typography>

          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={formData.criterionType}
              onChange={(e) => handleTypeChange(e.target.value)}
            >
              {Object.entries(evaluationTemplates).map(([key, template]) => (
                <Paper
                  key={key}
                  className={`${classes.typeOption} ${formData.criterionType === key ? classes.typeOptionSelected : ''}`}
                  onClick={() => handleTypeChange(key)}
                  elevation={0}
                >
                  <Box display="flex" alignItems="flex-start">
                    <Radio value={key} color="primary" style={{ padding: '0 12px 0 0' }} />
                    <Box flex={1}>
                      <Typography
                        variant="subtitle2"
                        style={{ fontWeight: 600, color: 'var(--color-deep-blue)' }}
                      >
                        {template.name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {template.description}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              ))}

              {/* Custom (Advanced) Option */}
              <Paper
                className={`${classes.typeOption} ${formData.criterionType === 'custom' ? classes.typeOptionSelected : ''}`}
                onClick={() => handleTypeChange('custom')}
                elevation={0}
              >
                <Box display="flex" alignItems="flex-start">
                  <Radio value="custom" color="primary" style={{ padding: '0 12px 0 0' }} />
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                      <Typography
                        variant="subtitle2"
                        style={{ fontWeight: 600, color: 'var(--color-deep-blue)' }}
                      >
                        Custom (Advanced)
                      </Typography>
                      <CodeIcon fontSize="small" style={{ color: 'var(--color-text-secondary)' }} />
                    </Box>
                    <Typography variant="body2" color="textSecondary">
                      Write your own evaluation prompt for full control
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </RadioGroup>
          </FormControl>
        </Box>

        {/* Structured Builder Section - for non-custom types */}
        {formData.criterionType !== 'custom' && getCurrentTemplate() && (
          <>
            {/* Check Items Selection */}
            <Box className={classes.checkItemsContainer}>
              <Typography variant="subtitle2" className={classes.sectionTitle}>
                What should the agent have done? (select all that apply)
              </Typography>
              <Box mt={1}>
                {getCurrentTemplate().checkItems?.map((item) => (
                  <Box key={item.key} className={classes.checkItemRow}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            formData.evaluationConfig.checkItems?.includes(item.key) || false
                          }
                          onChange={() => toggleCheckItem(item.key)}
                          color="primary"
                        />
                      }
                      label={item.label}
                      style={{ margin: 0, flex: 1 }}
                    />
                  </Box>
                ))}
              </Box>
              {formData.evaluationConfig.checkItems?.length > 0 && (
                <Typography
                  variant="caption"
                  style={{ color: '#059669', marginTop: 8, display: 'block' }}
                >
                  {formData.evaluationConfig.checkItems.length} item(s) selected
                </Typography>
              )}
            </Box>

            {/* Pass Criteria Selection */}
            <FormControl fullWidth variant="outlined" className={classes.passCriteriaSelect}>
              <InputLabel>Pass Criteria</InputLabel>
              <Select
                value={formData.evaluationConfig.passCriteria || 'any_2'}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    evaluationConfig: {
                      ...prev.evaluationConfig,
                      passCriteria: e.target.value,
                    },
                  }))
                }
                label="Pass Criteria"
                style={{ borderRadius: 12 }}
              >
                {getCurrentTemplate().passCriteriaOptions?.map((option) => (
                  <MenuItem key={option.key} value={option.key}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* N/A Condition */}
            <TextField
              label="N/A Condition (optional)"
              fullWidth
              variant="outlined"
              value={formData.evaluationConfig.naCondition || ''}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  evaluationConfig: {
                    ...prev.evaluationConfig,
                    naCondition: e.target.value,
                  },
                }))
              }
              placeholder="e.g., Mark as N/A if call was a wrong number or general inquiry"
              className={classes.naConditionField}
              helperText="When should this criterion be marked as Not Applicable?"
            />

            {/* Generated Prompt Preview */}
            {(generatedPrompt || promptLoading) && (
              <Box className={classes.generatedPromptPreview}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  marginBottom={1}
                >
                  <Typography variant="subtitle2" style={{ color: '#166534', fontWeight: 600 }}>
                    Generated Prompt Preview
                  </Typography>
                  {promptLoading && <CircularProgress size={16} style={{ color: '#166534' }} />}
                </Box>
                <Typography className={classes.promptPreviewText}>
                  {generatedPrompt || 'Generating preview...'}
                </Typography>
              </Box>
            )}
          </>
        )}

        {/* Custom Prompt Section - for custom type */}
        {formData.criterionType === 'custom' && (
          <Box className={classes.customPromptSection}>
            <TextField
              label="Evaluation Prompt"
              fullWidth
              multiline
              rows={8}
              margin="normal"
              variant="outlined"
              value={formData.evaluationPrompt}
              onChange={(e) => setFormData({ ...formData, evaluationPrompt: e.target.value })}
              placeholder="Enter the prompt that will be sent to the AI to evaluate this criterion..."
              className={classes.promptField}
            />

            <Box className={classes.variableHint}>
              <Typography
                variant="subtitle2"
                style={{ color: 'var(--color-deep-blue)', marginBottom: 8 }}
              >
                Template Variables (click to insert)
              </Typography>
              <Box display="flex" flexWrap="wrap">
                {TEMPLATE_VARIABLES.map((v) => (
                  <Tooltip key={v.key} title={v.description}>
                    <Chip
                      size="small"
                      label={v.key}
                      className={classes.variableChip}
                      onClick={() => insertVariable(v.key)}
                    />
                  </Tooltip>
                ))}
              </Box>
            </Box>

            <Alert severity="info" style={{ borderRadius: 12, marginTop: 16 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Important:</strong> Use template variables like{' '}
                <code>{'{{transcript}}'}</code> and <code>{'{{callNotes}}'}</code> to include call
                data in your prompt. These are not automatically added.
              </Typography>
              <Typography variant="body2">
                <strong>Response Format:</strong> Your prompt must ask the AI to respond with PASS,
                FAIL, or N_A with a reason. Example ending:
              </Typography>
              <Typography
                variant="body2"
                style={{ fontFamily: 'monospace', marginTop: 8, fontSize: 'var(--font-size-sm)' }}
              >
                Respond with exactly one of: PASS, FAIL, or N_A followed by a brief reason.
              </Typography>
            </Alert>
          </Box>
        )}

        {/* Test Section */}
        <Box className={classes.testSection}>
          <Box display="flex" alignItems="center" justifyContent="space-between" marginBottom={2}>
            <Box>
              <Typography
                variant="subtitle2"
                style={{ color: 'var(--color-deep-blue)', fontWeight: 600 }}
              >
                Test Your Prompt
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Test your prompt against a real call from{' '}
                {selectedCampaign?.displayName || 'the selected campaign'}
              </Typography>
            </Box>
            <Button
              size="small"
              onClick={insertSamplePrompt}
              style={{ textTransform: 'none', color: 'var(--color-deep-blue)' }}
            >
              Insert Sample Prompt
            </Button>
          </Box>

          {/* Call Selection Mode Toggle */}
          <Box display="flex" alignItems="center" marginBottom={2} style={{ gap: 8 }}>
            <Chip
              label="Select from list"
              onClick={() => {
                setCallInputMode('dropdown')
                setManualCallId('')
                setCallValidation(null)
                if (campaignCalls.length === 0 && selectedCampaign) {
                  fetchCampaignCalls()
                }
              }}
              style={{
                backgroundColor:
                  callInputMode === 'dropdown'
                    ? 'var(--color-deep-blue-bg)'
                    : 'var(--color-bg-gray)',
                color: callInputMode === 'dropdown' ? '#fff' : 'var(--color-text-secondary)',
                cursor: 'pointer',
              }}
            />
            <Chip
              label="Enter manually"
              onClick={() => {
                setCallInputMode('manual')
                setTestCallId('')
              }}
              style={{
                backgroundColor:
                  callInputMode === 'manual' ? 'var(--color-deep-blue-bg)' : 'var(--color-bg-gray)',
                color: callInputMode === 'manual' ? '#fff' : 'var(--color-text-secondary)',
                cursor: 'pointer',
              }}
            />
          </Box>

          {/* Call Selection */}
          {callInputMode === 'dropdown' ? (
            <Box marginBottom={2}>
              <AutoCompleteInput
                options={campaignCalls}
                getOptionKey={(option) => option.callId}
                getOptionLabel={(option) => {
                  const date = new Date(option.callDate).toLocaleDateString()
                  const caller = option.callerName || 'Unknown'
                  return `${option.callId} - ${caller} (${date})`
                }}
                loading={callsLoading}
                onChange={(event, newValue) => {
                  setTestCallId(newValue?.callId || '')
                }}
                renderOptionContent={(option) => (
                  <Box>
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      {option.callId}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {option.callerName || 'Unknown'} • {option.agentName || 'Unknown'} •{' '}
                      {new Date(option.callDate).toLocaleDateString()} •{' '}
                      {option.disposition || 'No disposition'}
                    </Typography>
                  </Box>
                )}
                showSearchIcon={false}
                textFieldProps={(params) => ({
                  size: 'small',
                  label: 'Select a call to test',
                  InputProps: {
                    ...params.InputProps,
                    style: { borderRadius: 10 },
                    endAdornment: (
                      <>
                        {callsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                })}
                placeholder="Search calls..."
                noOptionsText={
                  callsLoading ? 'Loading calls...' : 'No calls with transcripts found'
                }
              />
              {campaignCalls.length > 0 && (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{ marginTop: 4, display: 'block' }}
                >
                  Showing {campaignCalls.length} recent calls with transcripts
                </Typography>
              )}
            </Box>
          ) : (
            <Box marginBottom={2}>
              <Box display="flex" alignItems="flex-start" style={{ gap: 12 }}>
                <TextField
                  label="Call ID"
                  size="small"
                  variant="outlined"
                  value={manualCallId}
                  onChange={(e) => {
                    setManualCallId(e.target.value)
                    setCallValidation(null)
                    setTestCallId('')
                  }}
                  placeholder="Enter a call ID"
                  style={{ flex: 1 }}
                  InputProps={{ style: { borderRadius: 10 } }}
                  error={callValidation && !callValidation.valid}
                  helperText={callValidation && !callValidation.valid ? callValidation.reason : ''}
                />
                <Button
                  variant="outlined"
                  onClick={() => verifyCallId(manualCallId)}
                  disabled={validating || !manualCallId.trim()}
                  style={{
                    borderRadius: 10,
                    textTransform: 'none',
                    minWidth: 100,
                    height: 40,
                  }}
                >
                  {validating ? <CircularProgress size={20} /> : 'Verify'}
                </Button>
              </Box>
              {callValidation?.valid && (
                <Alert severity="success" style={{ marginTop: 8, borderRadius: 8 }}>
                  Call verified! Campaign: {callValidation.campaignName}
                </Alert>
              )}
            </Box>
          )}

          {/* Test Button */}
          <Button
            variant="contained"
            fullWidth
            startIcon={testLoading ? <CircularProgress size={16} color="inherit" /> : <TestIcon />}
            onClick={handleTestPrompt}
            disabled={
              testLoading ||
              !testCallId.trim() ||
              (formData.criterionType === 'custom'
                ? !formData.evaluationPrompt.trim()
                : !generatedPrompt.trim())
            }
            className={`${classes.actionButton} ${classes.primaryButton}`}
            style={{ marginBottom: 16 }}
          >
            {testLoading ? 'Testing...' : 'Run Test'}
          </Button>

          {/* Test Result */}
          {testResult && (
            <Box className={classes.testResult}>
              <Typography
                variant="subtitle2"
                gutterBottom
                style={{ color: 'var(--color-deep-blue)' }}
              >
                Test Result
              </Typography>
              {testResult.success ? (
                <>
                  <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 12 }}>
                    <Chip
                      label={testResult.result?.result || 'N/A'}
                      style={{
                        backgroundColor:
                          testResult.result?.result === 'PASS'
                            ? '#4CAF50'
                            : testResult.result?.result === 'FAIL'
                              ? '#EF4444'
                              : '#9E9E9E',
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                    {testResult.callData && (
                      <Typography variant="caption" color="textSecondary">
                        Call: {testResult.callData.callId} | Agent: {testResult.callData.agentName}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body2" style={{ marginBottom: 8 }}>
                    <strong>Reason:</strong> {testResult.result?.reason}
                  </Typography>
                  {testResult.result?.evidence && (
                    <Box
                      style={{
                        backgroundColor: 'var(--color-bg-gray)',
                        padding: 12,
                        borderRadius: 8,
                        marginTop: 8,
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        style={{ display: 'block', marginBottom: 4 }}
                      >
                        <strong>Evidence from transcript:</strong>
                      </Typography>
                      <Typography variant="body2" style={{ fontStyle: 'italic' }}>
                        &ldquo;{testResult.result?.evidence}&rdquo;
                      </Typography>
                    </Box>
                  )}
                </>
              ) : (
                <Alert severity="error" style={{ borderRadius: 8 }}>
                  {testResult.error || 'Test failed'}
                </Alert>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions
        style={{ padding: '16px 24px', borderTop: '1px solid var(--color-border-light)' }}
      >
        <Button onClick={onClose} style={{ color: 'var(--color-text-secondary)' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={
            saving ||
            !formData.criterionName.trim() ||
            (formData.criterionType === 'custom'
              ? !formData.evaluationPrompt.trim()
              : !formData.evaluationConfig.checkItems?.length) ||
            (!editingCriterion && selectedCampaignsForCreate.length === 0)
          }
          className={`${classes.actionButton} ${classes.primaryButton}`}
        >
          {saving ? (
            <CircularProgress size={20} color="inherit" />
          ) : editingCriterion ? (
            'Save Changes'
          ) : selectedCampaignsForCreate.length > 1 ? (
            `Create for ${selectedCampaignsForCreate.length} Campaigns`
          ) : (
            'Save Criterion'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const campaignShape = PropTypes.shape({
  name: PropTypes.string,
  displayName: PropTypes.string,
})

const callShape = PropTypes.shape({
  callId: PropTypes.string,
  callerName: PropTypes.string,
  agentName: PropTypes.string,
  callDate: PropTypes.string,
  disposition: PropTypes.string,
})

QACriteriaDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  editingCriterion: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    criterionName: PropTypes.string,
    criterionType: PropTypes.string,
    evaluationConfig: PropTypes.object,
    evaluationPrompt: PropTypes.string,
    isActive: PropTypes.bool,
  }),
  classes: PropTypes.object.isRequired,
  formData: PropTypes.shape({
    criterionName: PropTypes.string,
    criterionType: PropTypes.string,
    evaluationConfig: PropTypes.shape({
      checkItems: PropTypes.arrayOf(PropTypes.string),
      passCriteria: PropTypes.string,
      naCondition: PropTypes.string,
    }),
    evaluationPrompt: PropTypes.string,
    isActive: PropTypes.bool,
  }).isRequired,
  setFormData: PropTypes.func.isRequired,
  generatedPrompt: PropTypes.string.isRequired,
  promptLoading: PropTypes.bool.isRequired,
  evaluationTemplates: PropTypes.object.isRequired,
  allCampaigns: PropTypes.arrayOf(campaignShape).isRequired,
  selectedCampaign: campaignShape,
  selectedCampaignsForCreate: PropTypes.arrayOf(campaignShape).isRequired,
  setSelectedCampaignsForCreate: PropTypes.func.isRequired,
  getCriteriaCount: PropTypes.func.isRequired,
  toggleCheckItem: PropTypes.func.isRequired,
  handleTypeChange: PropTypes.func.isRequired,
  getCurrentTemplate: PropTypes.func.isRequired,
  insertVariable: PropTypes.func.isRequired,
  insertSamplePrompt: PropTypes.func.isRequired,
  testCallId: PropTypes.string.isRequired,
  setTestCallId: PropTypes.func.isRequired,
  testResult: PropTypes.shape({
    success: PropTypes.bool,
    error: PropTypes.string,
    result: PropTypes.shape({
      result: PropTypes.string,
      reason: PropTypes.string,
      evidence: PropTypes.string,
    }),
    callData: PropTypes.shape({
      callId: PropTypes.string,
      agentName: PropTypes.string,
    }),
  }),
  testLoading: PropTypes.bool.isRequired,
  handleTestPrompt: PropTypes.func.isRequired,
  campaignCalls: PropTypes.arrayOf(callShape).isRequired,
  callsLoading: PropTypes.bool.isRequired,
  callInputMode: PropTypes.oneOf(['dropdown', 'manual']).isRequired,
  setCallInputMode: PropTypes.func.isRequired,
  manualCallId: PropTypes.string.isRequired,
  setManualCallId: PropTypes.func.isRequired,
  callValidation: PropTypes.shape({
    valid: PropTypes.bool,
    reason: PropTypes.string,
    campaignName: PropTypes.string,
  }),
  setCallValidation: PropTypes.func.isRequired,
  validating: PropTypes.bool.isRequired,
  verifyCallId: PropTypes.func.isRequired,
  fetchCampaignCalls: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
}

export default QACriteriaDialog
